import React, { useEffect, useState } from "react";
import { Reveal, Overline } from "@/components/motion";
import { ShieldCheck, FileCheck2, BadgeCheck, ClipboardCheck, FileText, Loader2, ImageOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { breadcrumbSchema } from "@/lib/seo";

const FALLBACK_CERTS = [
  { code: "IEC", name: "Import Export Code", status: "Registered", description: "Authorisation for import & export operations across borders." },
  { code: "APEDA", name: "APEDA Registration", status: "Registered", description: "Agricultural & Processed Food Products Export Development Authority." },
  { code: "FSSAI", name: "FSSAI License", status: "Registered", description: "Food Safety and Standards Authority of India compliance." },
  { code: "GST", name: "GST Registration", status: "Registered", description: "Goods & Services Tax registration for domestic trade." },
  { code: "ISO", name: "ISO 9001 QMS", status: "Implemented", description: "ISO 9001-Aligned Process Framework." },
];

const COMMIT = [
  { icon: ShieldCheck, title: "Food Safety First", desc: "Compliance-driven handling and storage at every stage of the supply chain." },
  { icon: ClipboardCheck, title: "Full Traceability", desc: "Batch-level records from procurement through dispatch." },
  { icon: BadgeCheck, title: "Quality Assurance", desc: "Documented inspection of purity, moisture and admixture per consignment." },
];

function normalizeCertification(cert) {
  return cert.code === "ISO"
    ? { ...cert, name: "ISO 9001 QMS", status: "Implemented" }
    : cert;
}

function CertificateViewer({ cert }) {
  if (!cert) return null;
  const isPdf = (cert.file_type || "").includes("pdf") || (cert.file_name || "").toLowerCase().endsWith(".pdf");
  if (!cert.file_data) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-16 border border-dashed border-brand-line rounded-sm bg-brand-cream" data-testid="cert-placeholder">
        <ImageOff className="text-brand-muted mb-4" size={40} strokeWidth={1.4} />
        <p className="font-display text-2xl text-brand-green">Certificate coming soon</p>
        <p className="text-sm text-brand-muted mt-2 max-w-sm">
          The {cert.name} document will be available here shortly. Please reach out to our team for verified copies.
        </p>
      </div>
    );
  }
  if (isPdf) {
    return (
      <div
        className="relative w-full h-[70vh] rounded-sm border border-brand-line overflow-hidden select-none"
        onContextMenu={(event) => event.preventDefault()}
        onDragStart={(event) => event.preventDefault()}
        data-testid="cert-view-only"
      >
        <iframe title={cert.name} src={`${cert.file_data}#toolbar=0&navpanes=0`} className="w-full h-full" data-testid="cert-pdf" />
      </div>
    );
  }
  return (
    <div
      className="w-full max-h-[70vh] rounded-sm border border-brand-line overflow-hidden select-none"
      onContextMenu={(event) => event.preventDefault()}
      onDragStart={(event) => event.preventDefault()}
      data-testid="cert-view-only"
    >
      <img src={cert.file_data} alt={cert.name} draggable="false" className="w-full max-h-[70vh] object-contain" data-testid="cert-image" />
    </div>
  );
}

export default function FoodSafety() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/certifications");
        setCerts(Array.isArray(data) && data.length > 0 ? data.map(normalizeCertification) : FALLBACK_CERTS);
      } catch {
        setCerts(FALLBACK_CERTS);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openCert = (cert) => {
    setActive(cert);
    setOpen(true);
  };

  return (
    <div>
      <Seo
        title="Food Safety & Export Licenses — IEC, APEDA, FSSAI, ISO"
        description="SDRC Global's food-safety compliance and export certifications — IEC, APEDA, FSSAI, GST and ISO. Committed to traceability, quality assurance and food safety for global buyers."
        path="/food-safety"
        jsonLd={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Food Safety Licenses", path: "/food-safety" }])]}
      />
      <section className="pt-36 md:pt-44 pb-14 md:pb-20 bg-brand-green-dark">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Food Safety Licenses", path: "/food-safety" }]} className="mb-6" /></Reveal>
          <Reveal><Overline className="text-brand-gold-light mb-5">Compliance</Overline></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight text-brand-cream leading-none max-w-4xl">
              Food Safety &amp; Licenses
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-brand-cream/75 max-w-2xl">
              Regulatory compliance and food-safety discipline are central to how we operate. Click any certificate to view.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-cream py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-green" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {certs.map((l, i) => (
                <Reveal key={l.code} delay={(i % 4) * 0.06}>
                  <button
                    type="button"
                    onClick={() => openCert(l)}
                    data-testid={`license-${l.code}`}
                    className="group text-left bg-white border border-brand-line p-8 h-full w-full hover:border-brand-gold hover:-translate-y-1 transition-all duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                  >
                    <div className="flex items-center justify-between">
                      <FileCheck2 size={26} className="text-brand-green" strokeWidth={1.6} />
                      <span className={`text-[11px] uppercase tracking-[0.15em] font-semibold px-3 py-1 rounded-full ${l.status === "Registered" ? "bg-brand-green/10 text-brand-green" : "bg-brand-gold/15 text-brand-gold-dark"}`}>
                        {l.status}
                      </span>
                    </div>
                    <h3 className="font-display text-3xl font-medium text-brand-green mt-6">{l.code}</h3>
                    <p className="text-sm font-semibold text-brand-charcoal mt-1">{l.name}</p>
                    <p className="text-sm text-brand-muted mt-3 leading-relaxed">{l.description}</p>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-gold-dark mt-5 group-hover:gap-2.5 transition-all">
                      <FileText size={13} /> {l.code === "ISO" ? "View Process Document" : l.file_data ? "View certificate" : "Coming soon"}
                    </span>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-white border-t border-brand-line py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Overline className="text-brand-gold-dark mb-5">Our Commitment</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl sm:text-5xl font-medium text-brand-green tracking-tight max-w-3xl">
              Committed to food safety, traceability and quality assurance.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14">
            {COMMIT.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <div className="border-t-2 border-brand-gold pt-6">
                  <c.icon size={30} className="text-brand-green" strokeWidth={1.5} />
                  <h3 className="font-display text-2xl font-medium text-brand-green mt-5">{c.title}</h3>
                  <p className="text-brand-muted mt-3 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-brand-cream border-brand-line" data-testid="cert-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-medium text-brand-green">
              {active?.name} <span className="text-brand-gold">({active?.code})</span>
            </DialogTitle>
            <DialogDescription className="text-brand-muted">{active?.description}</DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <CertificateViewer cert={active} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
